/**
 * @license
 * @preserve
 *
 * MIT License
 * 
 * Copyright (c) 2022 KeeeX SAS
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * 
 * ----------------------------------------
 * 
 * ISC License from bitcoinjs/bip39 project and source
 * 
 * Copyright (c) 2014, Wei Lu <luwei.here@gmail.com> and Daniel Cousens <email@dcousens.com>
 * 
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 * 
 */
import { digestImmediate } from "@keeex/crypto/lib/digest.js";
import { Algorithms as DigestAlgorithms } from "@keeex/crypto/lib/digesttypes.js";
import { deriveKeyRaw } from "@keeex/crypto/lib/keys.js";
import { utf82buf8 } from "@keeex/js-utils/lib/uint8array.js";
import { randomBytes } from "@keeex/crypto/lib/random.js";
import { binaryToByte, byteLengthToBitLength, bytesToBinary, normalizeUTF8String } from "./utils.js";
export { randomBytes };
/** @deprecated Cryptographic providers are automatically registered on import */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const setCryptoProvider = (_provider) => { };
export const pbkdf2 = (password, salt, iterations, keylen, digestAlgorithm) => deriveKeyRaw(password, utf82buf8(salt), digestAlgorithm, iterations, keylen);
export const CHECKSUM_KEPT_BITS_DIVIDER = 32;
export const MAX_ENTROPY_LENGTH = 32;
export const checkEntropyLength = (entropyBuffer) => {
    const MIN_ENTROPY_LENGTH = 16;
    // Expect 32bits groups
    const ENTROPY_GROUPS = 4;
    if (entropyBuffer.byteLength < MIN_ENTROPY_LENGTH
        || entropyBuffer.byteLength > MAX_ENTROPY_LENGTH
        || entropyBuffer.byteLength % ENTROPY_GROUPS !== 0) {
        throw new Error("Entropy length invalid");
    }
};
export const deriveChecksumBits = async (entropyBuffer) => {
    checkEntropyLength(entropyBuffer);
    const digestBitsToKeep = byteLengthToBitLength(entropyBuffer.byteLength)
        / CHECKSUM_KEPT_BITS_DIVIDER;
    const digest = await digestImmediate(DigestAlgorithms.SHA256, entropyBuffer);
    const digestBits = bytesToBinary(Array.from(digest));
    return digestBits.substring(0, digestBitsToKeep);
};
/** Return the payload after checksum validation */
export const validateBitsChecksum = async (bitsString) => {
    // Split ENT / checksum
    const dividerIndex = Math.floor(bitsString.length / (CHECKSUM_KEPT_BITS_DIVIDER + 1))
        * CHECKSUM_KEPT_BITS_DIVIDER;
    const entropyBits = bitsString.substring(0, dividerIndex);
    const checksumBits = bitsString.substring(dividerIndex);
    // Get as many groups of 8 bits as possible, and the remaining bits as the last entry
    // eslint-disable-next-line prefer-named-capture-group, require-unicode-regexp
    const bitsGroups = entropyBits.match(/(.{1,8})/g);
    if (!bitsGroups)
        throw new Error("Unexpected state");
    const entropyBytes = bitsGroups.map(binaryToByte);
    const entropy = new Uint8Array(entropyBytes);
    const computedChecksum = await deriveChecksumBits(entropy);
    if (computedChecksum !== checksumBits)
        throw new Error("Invalid checksum");
    return entropy;
};
export const getSalt = (password) => normalizeUTF8String(`mnemonic${password ?? ""}`);
